import React, { useEffect, useState } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import useSettings from "../../hooks/useSettings";
import api from "../../services/api";
import { toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from '../../translate/i18n.js';
import PlansManagerImproved from "../PlansManager/PlansManagerImproved";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  sectionHeader: {
    background: theme.mode === 'dark' 
      ? 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' 
      : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  settingItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    transition: 'all 0.2s ease',
    height: '100%',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e3f2fd',
      borderColor: theme.mode === 'dark' ? theme.palette.primary.light : '#1976d2',
    },
  },
  settingLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  settingDescription: {
    fontSize: '11px',
    color: theme.palette.text.secondary || theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      '& fieldset': {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
  },
}));

export default function SubscriptionsConfig() {
  const classes = useStyles();
  const { update } = useSettings();

  const [paypalClientId, setPaypalClientId] = useState("");
  const [paypalClientSecret, setPaypalClientSecret] = useState("");
  const [paypalMode, setPaypalMode] = useState("sandbox");
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [manualPaymentEmail, setManualPaymentEmail] = useState("");
  const [manualPaymentWhatsapp, setManualPaymentWhatsapp] = useState("");
  const [manualPaymentInstructions, setManualPaymentInstructions] = useState("");
  const [loadingPaypalClientId, setLoadingPaypalClientId] = useState(false);
  const [loadingPaypalClientSecret, setLoadingPaypalClientSecret] = useState(false);
  const [loadingPaypalMode, setLoadingPaypalMode] = useState(false);
  const [loadingStripeSecretKey, setLoadingStripeSecretKey] = useState(false);
  const [loadingStripeWebhookSecret, setLoadingStripeWebhookSecret] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(false);
  const [loadingManualPaymentEmail, setLoadingManualPaymentEmail] = useState(false);
  const [loadingManualPaymentWhatsapp, setLoadingManualPaymentWhatsapp] = useState(false);
  const [loadingManualPaymentInstructions, setLoadingManualPaymentInstructions] = useState(false);

  const [binanceEnabled, setBinanceEnabled] = useState(false);
  const [binanceApiKey, setBinanceApiKey] = useState("");
  const [binanceApiSecret, setBinanceApiSecret] = useState("");
  const [binancePayId, setBinancePayId] = useState("");
  const [binanceQrUrl, setBinanceQrUrl] = useState("");
  const [binanceApiKeySet, setBinanceApiKeySet] = useState(false);
  const [binanceApiSecretSet, setBinanceApiSecretSet] = useState(false);
  const [binanceValidatedAt, setBinanceValidatedAt] = useState(null);
  const [loadingBinance, setLoadingBinance] = useState(false);
  const [testingBinance, setTestingBinance] = useState(false);

  useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        const globalSettingsKeys = [
          'paypalClientId',
          'paypalClientSecret',
          'paypalMode',
          'stripeSecretKey',
          'stripeWebhookSecret',
          'currency',
          'manualPaymentEmail',
          'manualPaymentWhatsapp',
          'manualPaymentInstructions'
        ];

        const settingsPromises = globalSettingsKeys.map(key =>
          api.get(`/settings/${key}`).catch(err => {
            // ✅ Si el setting no existe (404), retornar null en lugar de fallar
            if (err?.response?.status === 404) {
              return { data: null, status: 404 };
            }
            throw err;
          })
        );

        const settingsResults = await Promise.all(settingsPromises);

        // Payment gateways
        if (settingsResults[0]?.data?.value) {
          setPaypalClientId(settingsResults[0].data.value);
        }
        if (settingsResults[1]?.data?.value) {
          setPaypalClientSecret(settingsResults[1].data.value);
        }
        if (settingsResults[2]?.data?.value) {
          setPaypalMode(settingsResults[2].data.value);
        } else {
          setPaypalMode("sandbox"); // Valor por defecto
        }
        if (settingsResults[3]?.data?.value) {
          setStripeSecretKey(settingsResults[3].data.value);
        }
        if (settingsResults[4]?.data?.value) {
          setStripeWebhookSecret(settingsResults[4].data.value);
        }
        // ✅ Currency: Verificar si existe y tiene valor, si no usar por defecto
        if (settingsResults[5]?.data?.value) {
          setCurrency(settingsResults[5].data.value);
        } else if (settingsResults[5]?.status === 404 || !settingsResults[5]?.data) {
          // Si el setting no existe, usar valor por defecto
          setCurrency("USD");
        } else {
          setCurrency("USD"); // Valor por defecto
        }
        if (settingsResults[6]?.data?.value) {
          setManualPaymentEmail(settingsResults[6].data.value);
        }
        if (settingsResults[7]?.data?.value) {
          setManualPaymentWhatsapp(settingsResults[7].data.value);
        }
        if (settingsResults[8]?.data?.value) {
          setManualPaymentInstructions(settingsResults[8].data.value);
        }
      } catch (error) {
        console.warn('Error cargando settings globales, usando valores por defecto:', error);
      }
    };

    loadGlobalSettings();
    loadBinanceConfig();
  }, []);

  const loadBinanceConfig = async () => {
    try {
      const { data } = await api.get("/binance-pay/config");
      setBinanceEnabled(!!data.enabled);
      setBinancePayId(data.payId || "");
      setBinanceQrUrl(data.qrUrl || "");
      setBinanceApiKeySet(!!data.apiKeySet);
      setBinanceApiSecretSet(!!data.apiSecretSet);
      setBinanceValidatedAt(data.validatedAt || null);
      setBinanceApiKey("");
      setBinanceApiSecret("");
    } catch (error) {
      console.warn("Error cargando Binance Pay:", error);
    }
  };

  const handleSaveBinance = async () => {
    setLoadingBinance(true);
    try {
      const payload = {
        enabled: binanceEnabled,
        payId: binancePayId,
        qrUrl: binanceQrUrl,
      };
      if (binanceApiKey.trim()) payload.apiKey = binanceApiKey.trim();
      if (binanceApiSecret.trim()) payload.apiSecret = binanceApiSecret.trim();
      const { data } = await api.put("/binance-pay/config", payload);
      setBinanceEnabled(!!data.enabled);
      setBinancePayId(data.payId || "");
      setBinanceQrUrl(data.qrUrl || "");
      setBinanceApiKeySet(!!data.apiKeySet);
      setBinanceApiSecretSet(!!data.apiSecretSet);
      setBinanceValidatedAt(data.validatedAt || null);
      setBinanceApiKey("");
      setBinanceApiSecret("");
      toast.success(i18n.t("settings.subscriptionsPayment.toastBinance"));
    } catch (error) {
      toast.error(error?.response?.data?.error || i18n.t("settings.error"));
    } finally {
      setLoadingBinance(false);
    }
  };

  const handleTestBinance = async () => {
    setTestingBinance(true);
    try {
      const { data } = await api.post("/binance-pay/test");
      setBinanceValidatedAt(data.validatedAt || new Date().toISOString());
      toast.success(i18n.t("settings.subscriptionsPayment.toastBinanceTest"));
    } catch (error) {
      toast.error(error?.response?.data?.error || i18n.t("settings.error"));
    } finally {
      setTestingBinance(false);
    }
  };

  const uploadBinanceQr = async (file) => {
    if (!file) return;
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/files/upload-single", body);
      setBinanceQrUrl(data.path || data.url || "");
      toast.success(i18n.t("settings.subscriptionsPayment.binanceQrUploaded"));
    } catch (error) {
      toast.error(error?.response?.data?.error || i18n.t("settings.error"));
    }
  };

  async function handleSavePaypalClientId() {
    const value = paypalClientId;
    setLoadingPaypalClientId(true);
    try {
      await update({
        key: "paypalClientId",
        value,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastPaypalId"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando paypalClientId:', error);
    } finally {
      setLoadingPaypalClientId(false);
    }
  }

  async function handleSavePaypalClientSecret() {
    const value = paypalClientSecret;
    setLoadingPaypalClientSecret(true);
    try {
      await update({
        key: "paypalClientSecret",
        value,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastPaypalSecret"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando paypalClientSecret:', error);
    } finally {
      setLoadingPaypalClientSecret(false);
    }
  }

  async function handleSavePaypalMode(value) {
    setLoadingPaypalMode(true);
    try {
      await update({
        key: "paypalMode",
        value,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastPaypalMode"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando paypalMode:', error);
    } finally {
      setLoadingPaypalMode(false);
    }
  }

  async function handleSaveStripeSecretKey() {
    const value = stripeSecretKey;
    setLoadingStripeSecretKey(true);
    try {
      await update({
        key: "stripeSecretKey",
        value,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastStripeKey"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando stripeSecretKey:', error);
    } finally {
      setLoadingStripeSecretKey(false);
    }
  }

  async function handleSaveStripeWebhookSecret() {
    const value = stripeWebhookSecret;
    setLoadingStripeWebhookSecret(true);
    try {
      await update({
        key: "stripeWebhookSecret",
        value,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastStripeWebhook"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando stripeWebhookSecret:', error);
    } finally {
      setLoadingStripeWebhookSecret(false);
    }
  }

  async function handleSaveCurrency(value) {
    setLoadingCurrency(true);
    try {
      const result = await update({
        key: "currency",
        value,
      });
      // ✅ Actualizar el estado local con el valor guardado confirmado desde el servidor
      // El hook useSettings retorna responseData que es el objeto setting completo
      if (result?.value) {
        setCurrency(result.value);
      } else {
        // Si no viene el valor en result, usar el valor que se intentó guardar
        setCurrency(value);
      }
      toast.success(i18n.t("settings.subscriptionsPayment.toastCurrency"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando currency:', error);
      // ✅ Revertir el estado si hay error - recargar el valor actual desde el servidor
      try {
        const response = await api.get(`/settings/currency`);
        if (response?.data?.value) {
          setCurrency(response.data.value);
        } else {
          // Si no existe, mantener el valor actual del estado
          console.warn('Setting currency no encontrado, manteniendo valor actual del estado');
        }
      } catch (reloadError) {
        console.error('Error recargando currency:', reloadError);
        // Mantener el valor actual del estado si no se puede recargar
      }
    } finally {
      setLoadingCurrency(false);
    }
  }

  async function handleSaveManualPaymentEmail() {
    setLoadingManualPaymentEmail(true);
    try {
      await update({
        key: "manualPaymentEmail",
        value: manualPaymentEmail,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastManualEmail"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando manualPaymentEmail:', error);
    } finally {
      setLoadingManualPaymentEmail(false);
    }
  }

  async function handleSaveManualPaymentWhatsapp() {
    setLoadingManualPaymentWhatsapp(true);
    try {
      await update({
        key: "manualPaymentWhatsapp",
        value: manualPaymentWhatsapp,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastManualWhatsapp"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando manualPaymentWhatsapp:', error);
    } finally {
      setLoadingManualPaymentWhatsapp(false);
    }
  }

  async function handleSaveManualPaymentInstructions() {
    setLoadingManualPaymentInstructions(true);
    try {
      await update({
        key: "manualPaymentInstructions",
        value: manualPaymentInstructions,
      });
      toast.success(i18n.t("settings.subscriptionsPayment.toastManualInstructions"));
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando manualPaymentInstructions:', error);
    } finally {
      setLoadingManualPaymentInstructions(false);
    }
  }

  return (
    <Box className={classes.container}>
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label={i18n.t("settings.subscriptionsPayment.title")}>💳</span> {i18n.t("settings.subscriptionsPayment.title")}
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {i18n.t("settings.subscriptionsPayment.intro")}
            <strong> {i18n.t("settings.subscriptionsPayment.introNote")}</strong>
          </Typography>
          
          <Grid container spacing={2}>
            {/* PayPal Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: 16, marginTop: 8 }}>
                {i18n.t("settings.subscriptionsPayment.sectionPaypal")}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.paypalClientIdLabel")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.paypalClientIdDesc")}
                </Typography>
                <TextField
                  id="paypalClientId"
                  name="paypalClientId"
                  margin="dense"
                  label={i18n.t("settings.subscriptionsPayment.paypalClientIdField")}
                  variant="outlined"
                  type="password"
                  value={paypalClientId}
                  onChange={(e) => setPaypalClientId(e.target.value)}
                  onBlur={handleSavePaypalClientId}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingPaypalClientId ? i18n.t("settings.subscriptionsPayment.saving") : i18n.t("settings.subscriptionsPayment.paypalClientIdHelper")}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.paypalSecretLabel")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.paypalSecretDesc")}
                </Typography>
                <TextField
                  id="paypalClientSecret"
                  name="paypalClientSecret"
                  margin="dense"
                  label={i18n.t("settings.subscriptionsPayment.paypalSecretField")}
                  variant="outlined"
                  type="password"
                  value={paypalClientSecret}
                  onChange={(e) => setPaypalClientSecret(e.target.value)}
                  onBlur={handleSavePaypalClientSecret}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingPaypalClientSecret ? i18n.t("settings.subscriptionsPayment.saving") : i18n.t("settings.subscriptionsPayment.paypalSecretHelper")}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.paypalModeLabel")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.paypalModeDesc")}
                </Typography>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel>{i18n.t("settings.subscriptionsPayment.modeField")}</InputLabel>
                  <Select
                    value={paypalMode}
                    onChange={(e) => {
                      setPaypalMode(e.target.value);
                      handleSavePaypalMode(e.target.value);
                    }}
                    label={i18n.t("settings.subscriptionsPayment.modeField")}
                    disabled={loadingPaypalMode}
                  >
                    <MenuItem value="sandbox">{i18n.t("settings.subscriptionsPayment.modeSandbox")}</MenuItem>
                    <MenuItem value="live">{i18n.t("settings.subscriptionsPayment.modeLive")}</MenuItem>
                  </Select>
                  {loadingPaypalMode && (
                    <FormHelperText>{i18n.t("settings.subscriptionsPayment.saving")}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>

            {/* Stripe Configuration */}
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                {i18n.t("settings.subscriptionsPayment.sectionStripe")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.stripeKeyLabel")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.stripeKeyDesc")}
                </Typography>
                <TextField
                  id="stripeSecretKey"
                  name="stripeSecretKey"
                  margin="dense"
                  label={i18n.t("settings.subscriptionsPayment.stripeKeyField")}
                  variant="outlined"
                  type="password"
                  value={stripeSecretKey}
                  onChange={(e) => setStripeSecretKey(e.target.value)}
                  onBlur={handleSaveStripeSecretKey}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingStripeSecretKey ? i18n.t("settings.subscriptionsPayment.saving") : i18n.t("settings.subscriptionsPayment.stripeKeyHelper")}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.stripeWebhookLabel")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.stripeWebhookDesc")}
                </Typography>
                <TextField
                  id="stripeWebhookSecret"
                  name="stripeWebhookSecret"
                  margin="dense"
                  label={i18n.t("settings.subscriptionsPayment.stripeWebhookField")}
                  variant="outlined"
                  type="password"
                  value={stripeWebhookSecret}
                  onChange={(e) => setStripeWebhookSecret(e.target.value)}
                  onBlur={handleSaveStripeWebhookSecret}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingStripeWebhookSecret ? i18n.t("settings.subscriptionsPayment.saving") : i18n.t("settings.subscriptionsPayment.stripeWebhookHelper")}
                />
              </Box>
            </Grid>

            {/* Currency Configuration */}
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                {i18n.t("settings.subscriptionsPayment.sectionCurrency")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.currencyLabel")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.currencyDesc")}
                </Typography>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel>{i18n.t("settings.subscriptionsPayment.currencyField")}</InputLabel>
                  <Select
                    value={currency}
                    onChange={(e) => {
                      // ✅ No actualizar el estado local inmediatamente, esperar a que se guarde
                      handleSaveCurrency(e.target.value);
                    }}
                    label={i18n.t("settings.subscriptionsPayment.currencyField")}
                    disabled={loadingCurrency}
                  >
                    <MenuItem value="USD">{i18n.t("settings.subscriptionsPayment.currencyUSD")}</MenuItem>
                    <MenuItem value="EUR">{i18n.t("settings.subscriptionsPayment.currencyEUR")}</MenuItem>
                    <MenuItem value="GBP">{i18n.t("settings.subscriptionsPayment.currencyGBP")}</MenuItem>
                    <MenuItem value="BRL">{i18n.t("settings.subscriptionsPayment.currencyBRL")}</MenuItem>
                    <MenuItem value="MXN">{i18n.t("settings.subscriptionsPayment.currencyMXN")}</MenuItem>
                    <MenuItem value="ARS">{i18n.t("settings.subscriptionsPayment.currencyARS")}</MenuItem>
                    <MenuItem value="CLP">{i18n.t("settings.subscriptionsPayment.currencyCLP")}</MenuItem>
                    <MenuItem value="COP">{i18n.t("settings.subscriptionsPayment.currencyCOP")}</MenuItem>
                    <MenuItem value="PEN">{i18n.t("settings.subscriptionsPayment.currencyPEN")}</MenuItem>
                  </Select>
                  {loadingCurrency && (
                    <FormHelperText>{i18n.t("settings.subscriptionsPayment.saving")}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ margin: "24px 0 16px" }} />
              <Typography variant="h6" style={{ marginBottom: 8 }}>
                {i18n.t("settings.subscriptionsPayment.sectionBinance")}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                {i18n.t("settings.subscriptionsPayment.binanceIntro")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.binanceEnabled")}
                </Typography>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel>{i18n.t("settings.subscriptionsPayment.binanceEnabled")}</InputLabel>
                  <Select
                    value={binanceEnabled ? "true" : "false"}
                    onChange={(e) => setBinanceEnabled(e.target.value === "true")}
                    label={i18n.t("settings.subscriptionsPayment.binanceEnabled")}
                  >
                    <MenuItem value="false">{i18n.t("settings.subscriptionsPayment.binanceOff")}</MenuItem>
                    <MenuItem value="true">{i18n.t("settings.subscriptionsPayment.binanceOn")}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.binanceApiKey")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.binanceApiKeyDesc")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="password"
                  autoComplete="new-password"
                  value={binanceApiKey}
                  onChange={(e) => setBinanceApiKey(e.target.value)}
                  className={classes.textField}
                  placeholder={binanceApiKeySet ? i18n.t("settings.subscriptionsPayment.binanceKeySet") : ""}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.binanceApiSecret")}
                </Typography>
                <Typography className={classes.settingDescription}>
                  {i18n.t("settings.subscriptionsPayment.binanceApiSecretDesc")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="password"
                  autoComplete="new-password"
                  value={binanceApiSecret}
                  onChange={(e) => setBinanceApiSecret(e.target.value)}
                  className={classes.textField}
                  placeholder={binanceApiSecretSet ? i18n.t("settings.subscriptionsPayment.binanceKeySet") : ""}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.binancePayId")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  autoComplete="off"
                  value={binancePayId}
                  onChange={(e) => setBinancePayId(e.target.value)}
                  className={classes.textField}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={8}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.binanceQrUrl")}
                </Typography>
                <Box style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    autoComplete="off"
                    value={binanceQrUrl}
                    onChange={(e) => setBinanceQrUrl(e.target.value)}
                    className={classes.textField}
                    style={{ flex: 1, minWidth: 200 }}
                  />
                  <label>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => uploadBinanceQr(e.target.files?.[0])}
                    />
                    <Box
                      component="span"
                      style={{
                        display: "inline-block",
                        padding: "10px 14px",
                        border: "1px solid #ccc",
                        borderRadius: 8,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {i18n.t("settings.subscriptionsPayment.binanceUploadQr")}
                    </Box>
                  </label>
                </Box>
                {binanceQrUrl ? (
                  <img
                    src={binanceQrUrl}
                    alt="QR Binance"
                    style={{ marginTop: 12, maxWidth: 140, maxHeight: 140, borderRadius: 8 }}
                  />
                ) : null}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSaveBinance}
                  disabled={loadingBinance}
                  style={{
                    border: "none",
                    background: "#1976d2",
                    color: "#fff",
                    padding: "10px 18px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  {loadingBinance
                    ? i18n.t("settings.subscriptionsPayment.saving")
                    : i18n.t("settings.subscriptionsPayment.binanceSave")}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleTestBinance}
                  disabled={testingBinance || (!binanceApiKeySet && !binanceApiKey)}
                  style={{
                    border: "1px solid #ccc",
                    background: "#fff",
                    padding: "10px 18px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  {testingBinance
                    ? i18n.t("settings.subscriptionsPayment.saving")
                    : i18n.t("settings.subscriptionsPayment.binanceTest")}
                </Box>
                {binanceValidatedAt ? (
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("settings.subscriptionsPayment.binanceValidated").replace(
                      "{date}",
                      new Date(binanceValidatedAt).toLocaleString()
                    )}
                  </Typography>
                ) : null}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ margin: "24px 0 16px" }} />
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                {i18n.t("settings.subscriptionsPayment.sectionManual")}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                {i18n.t("settings.subscriptionsPayment.manualIntro")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.manualEmailLabel")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={manualPaymentEmail}
                  onChange={(e) => setManualPaymentEmail(e.target.value)}
                  className={classes.textField}
                  placeholder="facturacion@tuempresa.com"
                  onBlur={handleSaveManualPaymentEmail}
                  disabled={loadingManualPaymentEmail}
                  helperText={loadingManualPaymentEmail ? i18n.t("settings.subscriptionsPayment.saving") : ""}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.manualWhatsappLabel")}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={manualPaymentWhatsapp}
                  onChange={(e) => setManualPaymentWhatsapp(e.target.value)}
                  className={classes.textField}
                  placeholder="+57 300 000 0000 o https://wa.me/573000000000"
                  onBlur={handleSaveManualPaymentWhatsapp}
                  disabled={loadingManualPaymentWhatsapp}
                  helperText={loadingManualPaymentWhatsapp ? i18n.t("settings.subscriptionsPayment.saving") : ""}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.subscriptionsPayment.manualInstructionsLabel")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  variant="outlined"
                  value={manualPaymentInstructions}
                  onChange={(e) => setManualPaymentInstructions(e.target.value)}
                  className={classes.textField}
                  placeholder={i18n.t("settings.subscriptionsPayment.manualInstructionsPlaceholder")}
                  onBlur={handleSaveManualPaymentInstructions}
                  disabled={loadingManualPaymentInstructions}
                  helperText={loadingManualPaymentInstructions ? i18n.t("settings.subscriptionsPayment.saving") : ""}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider style={{ margin: "24px 0" }} />

      <Box style={{ marginTop: 8 }}>
        <PlansManagerImproved />
      </Box>
    </Box>
  );
}
